import React, { useEffect, useState } from 'react';
import { ArrowLeft, LayoutGrid, Search } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import TopicIcon from '../../shared/icons/TopicIcon';
import { TOPICS, TOPIC_GROUPS, topicsOf, topicNav } from '../../../data/educationTaxonomy';
import { isViewableContent } from '../../../utils/educationContent';

/*
 * Browse All Content: every topic as a tile, grouped in ABA-outline order. Lives on its own
 * page so the Education home stays short and personal. Counts are fetched once per session.
 */

let countsCache = null;
const loadCounts = async () => {
  if (countsCache) return countsCache;
  const snap = await getDocs(collection(db, 'education_content'));
  const counts = {};
  snap.forEach((d) => {
    const data = { id: d.id, ...d.data() };
    if (!isViewableContent(data)) return;
    topicsOf(data).forEach((t) => { counts[t.id] = (counts[t.id] || 0) + 1; });
  });
  countsCache = counts;
  return counts;
};

/**
 * Three-up icon tiles with short labels. Topics with no content stay behind one line
 * ("N more topics, no content yet") so the page is sized for the library that exists.
 */
export const TopicGrid = ({ counts, loaded, filter = '', onTopicClick, showEmpty, onShowEmpty }) => {
  const q = filter.trim().toLowerCase();
  const all = TOPICS
    .map((t) => ({ ...topicNav(t), group: t.group, count: counts[t.id] || 0 }))
    .filter((t) => !q || t.name.toLowerCase().includes(q) || (t.short || '').toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
  const hiding = loaded && !q && !showEmpty;
  const visible = hiding ? all.filter((t) => t.count > 0) : all;
  const hidden = all.length - visible.length;
  return (
    <div className="space-y-5">
      {TOPIC_GROUPS.map((group) => {
        const inGroup = visible.filter((t) => t.group === group.id);
        if (inGroup.length === 0) return null;
        return (
          <div key={group.id}>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">{group.label}</h3>
            <div className="grid grid-cols-3 gap-2.5">
              {inGroup.map((topic) => (
                <button
                  key={topic.id}
                  onClick={() => { onTopicClick(topic); }}
                  className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 px-2 pt-3 pb-2.5 shadow-card hover:border-emerald-500 dark:hover:border-emerald-600 hover:shadow-card-hover transition-all text-center active:scale-[0.97] flex flex-col items-center"
                >
                  <div className={`w-11 h-11 bg-gradient-to-br ${topic.color} rounded-xl flex items-center justify-center shadow-md mb-2`}>
                    <TopicIcon type={topic.type} className="w-6 h-6 text-white" />
                  </div>
                  <div className="font-semibold text-[12.5px] leading-tight text-gray-900 dark:text-white line-clamp-2">{topic.short || topic.name}</div>
                  <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 tabular-nums">{topic.count}</div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {hiding && hidden > 0 && (
        <button onClick={onShowEmpty} className="w-full text-sm font-medium text-gray-500 dark:text-gray-400 py-2 hover:text-emerald-700 dark:hover:text-emerald-400">
          {hidden} more {hidden === 1 ? 'topic' : 'topics'}, no content yet
        </button>
      )}
      {visible.length === 0 && q && <p className="text-sm text-gray-500 dark:text-gray-400">No topic matches.</p>}
    </div>
  );
};

const EducationBrowseScreen = ({ onBack, onTopicClick }) => {
  const [counts, setCounts] = useState(countsCache || {});
  const [loaded, setLoaded] = useState(Boolean(countsCache));
  const [showEmpty, setShowEmpty] = useState(false);
  const [filter, setFilter] = useState('');
  useEffect(() => { loadCounts().then((c) => { setCounts(c); setLoaded(true); }).catch(() => setLoaded(true)); }, []);

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 dark:from-emerald-900 dark:to-black text-white p-6 pb-6">
        <button onClick={() => { onBack(); }} className="flex items-center gap-2 text-white mb-4 hover:opacity-80">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="flex items-center gap-4 mb-5">
          <div className="p-3 bg-amber-400/20 rounded-xl">
            <LayoutGrid size={28} className="text-amber-300" />
          </div>
          <div>
            <h1 className="font-merriweather text-2xl font-bold">Browse All Content</h1>
            <p className="text-emerald-100 text-sm mt-1">{TOPICS.length} topics</p>
          </div>
        </div>
        <div className="bg-emerald-900/40 dark:bg-gray-800/60 rounded-xl border-2 border-emerald-700/50 dark:border-gray-700 focus-within:border-amber-400/50 transition-colors">
          <div className="relative">
            <Search className="absolute left-4 top-3 text-emerald-300" size={18} />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Find a topic"
              className="w-full pl-11 pr-4 py-3 rounded-xl focus:outline-none text-white placeholder-emerald-300 bg-transparent text-[15px]"
            />
          </div>
        </div>
      </div>
      <div className="px-6 py-6">
        {loaded ? (
          <TopicGrid counts={counts} loaded={loaded} filter={filter} onTopicClick={onTopicClick} showEmpty={showEmpty} onShowEmpty={() => setShowEmpty(true)} />
        ) : (
          <div className="grid grid-cols-3 gap-2.5">{[...Array(6)].map((_, i) => <div key={i} className="h-24 rounded-2xl bg-gray-200/70 dark:bg-gray-800 animate-pulse" />)}</div>
        )}
      </div>
    </div>
  );
};

export default EducationBrowseScreen;
